import React, { useEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Chip, Menu } from "@mui/material"
import { ResourceFilterElement, ResourceFilterElementValue } from "../../../createOrderTypes"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { PlusMinusButtonFilter } from "./PlusMinusButtonFilter"
import { DateTimePickerFilter } from "./DateTimePickerFilter"
import { MultiSelectFilter } from "./MultiSelectFilter"
import { TimeSliderFilter } from "./TimeSliderFilter"
import { SwitcherFilter } from "./SwitcherFilter"
import { FilterIcon } from "./FilterIcon"

const useStyles = makeStyles()((theme) => ({
    chip: {
        borderRadius: ".75rem",
        height: "2.5rem",
        padding: "0 1rem",
        color: theme.palette.common.white,
        "& .MuiChip-label": {
            paddingLeft: ".5rem",
            fontSize: ".875rem",
        },
        "& .MuiChip-deleteIcon": {
            fontSize: "1.25rem",
            marginRight: "0",
            transition: "all ease-out 0.3s",
        },
    },
    selectedChip: {
        background: theme.palette.tailwind.slate[800],
        "&:hover": {
            background: theme.palette.tailwind.slate[900],
        },
        "& .MuiChip-deleteIcon": {
            color: theme.palette.tailwind.slate[300],
            "&:hover": {
                color: theme.palette.tailwind.slate[200],
            },
        },
    },
    unselectedChip: {
        background: theme.palette.common.white,
        "&:hover": {
            background: theme.palette.tailwind.slate[100],
        },
        "& .MuiChip-deleteIcon": {
            color: theme.palette.tailwind.slate[400],
            "&:hover": {
                color: theme.palette.tailwind.slate[500],
            },
        },
        color: theme.palette.common.black,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
}))

type FilterChipProps = {
    filterType: string
    onRemove: () => void
    resourceFiltersResult: ResourceFilterElement[]
    isActive: boolean
    setActiveFilter: (filter: string | null) => void
    initialValues: ResourceFilterElementValue[]
    isRemovable?: boolean
    onSave: (values: ResourceFilterElementValue[]) => void
}

export function FilterChip({
    filterType,
    onRemove,
    resourceFiltersResult,
    isActive,
    setActiveFilter,
    initialValues,
    onSave,
    isRemovable = true,
}: FilterChipProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [localValues, setLocalValues] = useState(initialValues)
    const chipRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (isActive && chipRef.current) {
            setAnchorEl(chipRef.current)
        }
    }, [isActive])

    useEffect(() => {
        if (!anchorEl && localValues.length === 0 && !isActive) {
            onRemove()
        }
    }, [anchorEl, localValues, onRemove])

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
        setActiveFilter(filterType)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
        setActiveFilter(null)
    }

    const filterData = resourceFiltersResult.find((filter) => filter.identifier === filterType)

    const handleToggleValue = (value: { key: string | number; value: string | number }) => {
        setLocalValues((prev) => {
            const newValues = prev.some((v) => v.key === value.key)
                ? prev.filter((v) => v.key !== value.key)
                : [...prev, value]
            onSave(newValues)
            return newValues
        })
    }

    const formatDuration = (minutes: number) => {
        const hours = Math.floor(minutes / 60)
        const remainingMinutes = minutes % 60
        return `${hours > 0 ? `${hours}h ` : ""}${remainingMinutes > 0 ? `${remainingMinutes}m` : ""}`
    }

    const hasSelectedValues = localValues.length > 0

    const getFilterLabel = (
        filterType: string,
        localValues: ResourceFilterElementValue[],
        hasSelectedValues: boolean,
        filterData?: ResourceFilterElement,
    ) => {
        if (!hasSelectedValues) {
            return filterData?.name || filterType
        }

        switch (filterType) {
            case "meetingDuration":
                return localValues.map((v) => formatDuration(Number(v.value))).join(", ")
            case "startDateTime":
                return localValues
                    .map((v) => {
                        const keyAsString = typeof v?.key === "string" ? v.key : String(v?.key)
                        return dateTimeFactory.formatDateTime(dateTimeFactory.fromISO(keyAsString))
                    })
                    .join(", ")

            default:
                return localValues.map((v) => v.key).join(", ")
        }
    }

    return (
        <>
            <Chip
                ref={chipRef}
                label={getFilterLabel(filterType, localValues, hasSelectedValues, filterData)}
                onClick={handleOpenMenu}
                onDelete={isRemovable ? onRemove : undefined}
                avatar={<FilterIcon filterType={filterType} isLight={hasSelectedValues} />}
                className={cx(classes.chip, hasSelectedValues ? classes.selectedChip : classes.unselectedChip)}
            />
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
                {(filterData?.elementType === "MultiSelect" || filterData?.elementType === "CheckBox") &&
                    !!filterData?.values.length && (
                        <MultiSelectFilter
                            identifier={filterType}
                            values={filterData.values}
                            localValues={localValues}
                            onToggle={handleToggleValue}
                        />
                    )}
                {filterData?.elementType === "PlusMinusButton" && (
                    <PlusMinusButtonFilter
                        identifier={filterType}
                        value={localValues[0]?.value !== undefined ? Number(localValues[0]?.value) : null}
                        setValue={(newValue) => {
                            const updatedValues = [{ key: String(newValue ?? ""), value: newValue ?? 0 }]
                            setLocalValues(updatedValues)
                            onSave(updatedValues)
                        }}
                    />
                )}
                {filterData?.elementType === "TimeSlider" && (
                    <TimeSliderFilter
                        identifier={filterType}
                        value={localValues[0]?.value !== undefined ? Number(localValues[0]?.value) : null}
                        setValue={(newValue) => {
                            const updatedValues = [{ key: String(newValue ?? ""), value: newValue ?? 0 }]
                            setLocalValues(updatedValues)
                            onSave(updatedValues)
                        }}
                    />
                )}
                {filterData?.elementType === "DateTimePicker" && (
                    <DateTimePickerFilter
                        value={localValues[0]?.value !== undefined ? localValues[0]?.value.toString() : null}
                        onClose={handleCloseMenu}
                        setValue={(newValue) => {
                            const updatedValues = [{ key: String(newValue ?? ""), value: newValue ?? 0 }]
                            setLocalValues(updatedValues)
                            onSave(updatedValues)
                        }}
                    />
                )}
                {filterData?.elementType === "Switcher" && (
                    <SwitcherFilter
                        identifier={filterType}
                        value={localValues[0]?.value !== undefined ? localValues[0]?.value.toString() : null}
                        setValue={(newValue) => {
                            const updatedValues = [{ key: newValue, value: newValue }]
                            setLocalValues(updatedValues)
                            onSave(updatedValues)
                        }}
                    />
                )}
            </Menu>
        </>
    )
}
